import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import EditIcon from '@mui/icons-material/Edit';
import { TunnelView, deleteTunnel, listTunnels } from './api';
import { formatBytes, hostOf, relativeTime } from './format';
import { TunnelDialog } from './TunnelDialog';

/** Same cadence as the Devices tab. */
const POLL_MS = 10_000;

function StatusChip({ tunnel }: { tunnel: TunnelView }) {
  return tunnel.online ? (
    <Chip label="online" size="small" color="success" />
  ) : (
    <Chip label="offline" size="small" variant="outlined" />
  );
}

/**
 * Tunnels tab (T9/T14): public HTTP forwarding at a glance. Ephemeral rows are
 * CLI-owned — they get Kill but no Edit, because editing something that dies
 * with a Ctrl+C is a lie.
 */
export function TunnelsTable() {
  const queryClient = useQueryClient();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<TunnelView | null>(null);
  const [killTarget, setKillTarget] = useState<TunnelView | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const tunnels = useQuery({
    queryKey: ['tunnels'],
    queryFn: listTunnels,
    refetchInterval: POLL_MS,
  });

  const kill = useMutation({
    mutationFn: deleteTunnel,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['tunnels'] });
      setKillTarget(null);
    },
  });

  function openCreate() {
    setEditing(null);
    setDialogOpen(true);
  }

  // Loading only blanks the table on the FIRST load; a poll keeps stale rows.
  if (tunnels.isLoading) {
    return (
      <Paper elevation={1}>
        <Box sx={{ display: 'grid', placeItems: 'center', p: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  const rows = tunnels.data ?? [];

  return (
    <>
      {tunnels.isError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => void tunnels.refetch()}>
              Retry
            </Button>
          }
        >
          {tunnels.error instanceof Error ? tunnels.error.message : 'could not load tunnels'}
        </Alert>
      )}
      {kill.isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {kill.error instanceof Error ? kill.error.message : 'could not kill the tunnel'}
        </Alert>
      )}

      <Paper elevation={1}>
        {rows.length === 0 ? (
          <Box sx={{ p: 6, textAlign: 'center' }}>
            <Typography color="text.secondary" gutterBottom>
              No tunnels yet.
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Publish an app on an enrolled device, or run <code>dkn expose 3000</code> for a
              temporary link.
            </Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
              New tunnel
            </Button>
          </Box>
        ) : (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 1 }}>
              <Button startIcon={<AddIcon />} onClick={openCreate}>
                New tunnel
              </Button>
            </Box>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Status</TableCell>
                    <TableCell>URL</TableCell>
                    <TableCell>Device / peer</TableCell>
                    <TableCell>Local target</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Auth</TableCell>
                    <TableCell>Traffic</TableCell>
                    <TableCell>Last active</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((tunnel) => (
                    <TableRow
                      key={tunnel.id}
                      hover
                      // Offline rows keep their last-known traffic, greyed.
                      sx={{ opacity: tunnel.online ? 1 : 0.6 }}
                    >
                      <TableCell>
                        <StatusChip tunnel={tunnel} />
                      </TableCell>
                      <TableCell>
                        <Tooltip title={copied === tunnel.id ? 'Copied' : 'Copy the URL'}>
                          <Button
                            size="small"
                            onClick={() => {
                              void navigator.clipboard?.writeText(tunnel.url);
                              setCopied(tunnel.id);
                            }}
                            sx={{ textTransform: 'none', fontFamily: 'monospace' }}
                          >
                            {hostOf(tunnel.url)}
                          </Button>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {tunnel.mode === 'ephemeral'
                          ? `${tunnel.createdBy}'s CLI (dkn expose)`
                          : (tunnel.deviceName ?? '—')}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {tunnel.localHost}:{tunnel.localPort}
                      </TableCell>
                      <TableCell>
                        {tunnel.mode === 'ephemeral' ? (
                          <Chip label="ephemeral" size="small" color="secondary" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            persistent
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {tunnel.authMode === 'basic' ? (
                          <Chip label="basic" size="small" color="warning" />
                        ) : (
                          '—'
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {formatBytes(tunnel.bytesIn)} ↓ · {formatBytes(tunnel.bytesOut)} ↑
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        {relativeTime(tunnel.lastActiveAt)}
                      </TableCell>
                      <TableCell align="right">
                        {tunnel.mode === 'persistent' && (
                          <Tooltip title="Edit this mapping">
                            <IconButton
                              size="small"
                              aria-label={`Edit ${tunnel.subdomain}`}
                              onClick={() => {
                                setEditing(tunnel);
                                setDialogOpen(true);
                              }}
                            >
                              <EditIcon fontSize="inherit" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Kill — drops live connections and deletes the URL">
                          <IconButton
                            size="small"
                            color="error"
                            aria-label={`Kill ${tunnel.subdomain}`}
                            onClick={() => setKillTarget(tunnel)}
                          >
                            <DeleteForeverIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Paper>

      <TunnelDialog
        open={dialogOpen}
        tunnel={editing}
        onClose={() => {
          setDialogOpen(false);
          setEditing(null);
        }}
      />

      <Dialog open={killTarget !== null} onClose={() => setKillTarget(null)}>
        <DialogTitle>Kill {killTarget ? hostOf(killTarget.url) : ''}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Live connections drop immediately and the URL stops answering.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKillTarget(null)}>Cancel</Button>
          <Button
            color="error"
            variant="contained"
            disabled={kill.isPending}
            onClick={() => killTarget && kill.mutate(killTarget.id)}
          >
            {kill.isPending ? 'Killing…' : 'Kill tunnel'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
